import { getLocalStorageItem } from '../../helpers/localStorageHelper'
import { isLocalDomain } from '../../helpers/domainHelper'
import { IDENTITY_SDK_VERSION } from '../../version'

export function checkUrlParams(param: string): string | null {
  if (window.location && window.location.search) {
    const values = window.location.search
    const urlParams = new URLSearchParams(values)
    return urlParams.get(param)
  }
  return null
}

export function getIdentityConfigOverrides(): any | undefined {
  const config = checkUrlParams('identityConfigOverrides')
  return config ? JSON.parse(atob(config)) : undefined
}

export function getActivateOverridesFromCookie(
  currentHref: string | undefined,
  activateQueryCookieValue: string | null,
) {
  if (currentHref?.includes('/activate') && activateQueryCookieValue) {
    const values = activateQueryCookieValue.split(',')
    const key = values[0].split('=')[1]
    const version = values[1].split('=')[1]
    const env = values[3].split('=')[1]
    return {
      key, version, env, crossDomain: true,
    }
  }
  return null
}

export function getVersionNumber(version?: string | null): string {
  if (!version) return (IDENTITY_SDK_VERSION as string)?.replace(/^v/, '')
  return String(version).replace(/^v/, '')
}

export function updatePropertiesWithUrlParams(current: {
  native: boolean
  useBaseSdkLocation: boolean
  version: string
  checkoutEnabled: boolean
  configLocationHost: string | null
  debug: boolean
  env: string | null
  language: string | null
  preview: boolean | null
  key: string | null
  clientId: string | null
}) {
  let registrationReferrer: string | null = null
  let referringPage: string | null = null

  const updated = { ...current }

  if (window.location && window.location.search) {
    const urlParams = window.location.search
    const paramObj = new URLSearchParams(urlParams)

    updated.native = paramObj.get('native') === 'true'
    updated.useBaseSdkLocation = paramObj.get('useBaseSdkLocation') === 'true'
    if (paramObj.get('version')) updated.version = getVersionNumber(paramObj.get('version'))
    updated.checkoutEnabled = paramObj.get('checkoutEnabled') === 'true'
    updated.configLocationHost = paramObj.get('configLocationHost')
      ? paramObj.get('configLocationHost')
      : updated.configLocationHost
    updated.debug = paramObj.get('debug') === 'true'
    updated.env = paramObj.get('env') ? paramObj.get('env') : updated.env
    updated.language = paramObj.get('language') ? paramObj.get('language') : updated.language
    updated.preview = paramObj.get('preview') === 'true'
    updated.key = paramObj.get('key') ? paramObj.get('key') : updated.key
    updated.clientId = paramObj.get('clientId') ? paramObj.get('clientId') : updated.clientId

    registrationReferrer = paramObj.get('registrationReferrer')
    referringPage = paramObj.get('referringPage')
  }

  const shouldAlsoUpdateFromActivate = window?.location?.href?.includes('/activate') || false

  return {
    updated,
    registrationReferrer,
    referringPage,
    shouldAlsoUpdateFromActivate,
  }
}

export function getAuthenticationUrl(options: {
  crossDomain: boolean
  useQueryStringSDKVersion: boolean | undefined
  env: string | null
  version: string
  useBaseSdkLocation: boolean
  server: { getSdkUrl: (path: string) => URL }
}): URL {
  const {
    crossDomain, useQueryStringSDKVersion, env, version, useBaseSdkLocation, server,
  } = options

  if (typeof window !== 'undefined' && window.location) {
    if (crossDomain || window.location.href.includes('nbcuni.com')) {
      let urlPrefix: string

      if (!useBaseSdkLocation && isLocalDomain()) {
        urlPrefix = `${window.location.hostname}:4000`
      } else if (env === 'production') {
        urlPrefix = 'id.nbcuni.com'
      } else {
        urlPrefix = `${env}.id-envs.nbcuni.com`
      }

      if (crossDomain && useQueryStringSDKVersion && !isLocalDomain()) {
        return new URL(`https://${urlPrefix}/iam/authenticate/`)
      }
      return new URL(`https://${urlPrefix}/websdk/v${version}/authenticate/`)
    }
  }

  if (!useBaseSdkLocation && isLocalDomain()) {
    return new URL(`https://${window.location.hostname}:4000`)
  }

  return server.getSdkUrl(`v${version}/authenticate/`)
}

export function getLocalStorageSdkLocalConfig() {
  return getLocalStorageItem('sdkLocalConfig')
}
